import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Checkbox } from 'semantic-ui-react';
import './index.css';

export const CHECKBOX_DISPLAY_CHECKED = 'CHECKBOX_DISPLAY_CHECKED';
export const CHECKBOX_DISPLAY_NOT_CHECKED = 'CHECKBOX_DISPLAY_NOT_CHECKED';
export const CHECKBOX_DISPLAY_INDETERMINATE = 'CHECKBOX_DISPLAY_INDETERMINATE';

/**
 * Dropdown bulk select
 * * This component build a bulk select options.
 * * If only the options "all" and "none" are passed, then the component will
 * be shown like as check box.
 * * If other options are shown, then the component will  be shown like as dropdown menu
 * * The header of the menu is a checkbox component.
 * * The parameter checkboxDisplay decides how the checkbox is marked: checked, not checked,
 * indeterminate
 * The constants CHECKBOX_DISPLAY_CHECKED, CHECKBOX_DISPLAY_NOT_CHECKED and
 * CHECKBOX_DISPLAY_INDETERMINATE
 * could be imported together with the component
 * Parameters (props)
 * @param string checkboxDisplay: [CHECKBOX_DISPLAY_CHECKED, CHECKBOX_DISPLAY_NOT_CHECKED,
 *        CHECKBOX_DISPLAY_INDETERMINATE]. default: CHECKBOX_DISPLAY_NOT_CHECKED
 * @param string labelAll = label for the option select all
 * @param func onClickAll = on click function by the option select all
 * @param string labelNone = label for the option deselect all
 * @param func onClickNone = on click function by the option deselect all
 * @param array dropdownOptions = [{text, onClick, checkboxDisplay}]
 */
export default class DropdownBulkSelect extends Component {
  constructor(props) {
    super(props);
    const { checkboxDisplay } = this.props;
    DropdownBulkSelect.getDerivedStateFromProps =
      DropdownBulkSelect.getDerivedStateFromProps.bind(this);
    this.selectOption = this.selectOption.bind(this);
    this.createMenuOptions = this.createMenuOptions.bind(this);
    this.selectCheckboxDisplay = this.selectCheckboxDisplay.bind(this);
    this.state = {
      checkboxDisplay,
    };
  }

  static getDerivedStateFromProps(nextProps, nextState) {
    const { checkboxDisplay } = nextProps;
    if (nextState.selectedOption) {
      return {
        checkboxDisplay: nextState.checkboxDisplay,
        selectedOption: false,
      };
    }

    if (checkboxDisplay !== nextState.checkboxDisplay) {
      return {
        checkboxDisplay,
      };
    }

    return null;
  }

  /**
   * Select option
   * @param object option
   */
  selectOption(option) {
    let { checkboxDisplay } = option;
    if (!checkboxDisplay) {
      checkboxDisplay = CHECKBOX_DISPLAY_NOT_CHECKED;
    }
    option.onClick();
    this.setState({
      checkboxDisplay,
      selectedOption: true,
    });
  }

  /**
   * options
   * Create the menu options
   * @return array
   */
  createMenuOptions() {
    const { dropdownOptions, labelAll, onClickAll, labelNone, onClickNone } =
      this.props;

    const options = [
      {
        key: labelAll.toString(),
        text: labelAll,
        onClick: () =>
          this.selectOption({
            checkboxDisplay: CHECKBOX_DISPLAY_CHECKED,
            onClick: onClickAll,
          }),
      },
      {
        key: labelNone.toString(),
        text: labelNone,
        onClick: () =>
          this.selectOption({
            checkboxDisplay: CHECKBOX_DISPLAY_NOT_CHECKED,
            onClick: onClickNone,
          }),
      },
    ];
    dropdownOptions.forEach((option) => {
      options.push({
        key: option.text.toString(),
        text: option.text,
        onClick: () => this.selectOption(option),
      });
    });

    return options;
  }

  /**
   * Select check box display
   * Choose how the checkbox element is marked
   */
  selectCheckboxDisplay() {
    const { checkboxDisplay } = this.state;
    let checked = false;
    let indeterminate = false;
    if (checkboxDisplay === CHECKBOX_DISPLAY_CHECKED) {
      checked = true;
    } else if (checkboxDisplay === CHECKBOX_DISPLAY_INDETERMINATE) {
      indeterminate = true;
    }
    return { checked, indeterminate };
  }

  render() {
    const { dropdownOptions, onClickNone, onClickAll } = this.props;
    const { indeterminate, checked } = this.selectCheckboxDisplay();
    /* if there are other options (exclude all and none), it will be
    shown a dropdown menu */
    if (dropdownOptions.length > 0) {
      const options = this.createMenuOptions();
      const trigger = (
        <Checkbox checked={checked} indeterminate={indeterminate} />
      );
      return (
        <Dropdown
          trigger={trigger}
          options={options}
          className="dropdown-bulk-select"
        />
      );
    }

    /* if the only options are none and all, it will be shown only a checkbox */
    return (
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onClick={
          checked
            ? () =>
                this.selectOption({
                  checkboxDisplay: CHECKBOX_DISPLAY_NOT_CHECKED,
                  onClick: onClickNone,
                })
            : () =>
                this.selectOption({
                  checkboxDisplay: CHECKBOX_DISPLAY_CHECKED,
                  onClick: onClickAll,
                })
        }
      />
    );
  }
}

DropdownBulkSelect.propTypes = {
  checkboxDisplay: PropTypes.string,
  labelAll: PropTypes.string.isRequired,
  onClickAll: PropTypes.func.isRequired,
  labelNone: PropTypes.string.isRequired,
  onClickNone: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      onClick: PropTypes.func,
      checkboxDisplay: PropTypes.string.isRequired,
    })
  ),
};

DropdownBulkSelect.defaultProps = {
  checkboxDisplay: CHECKBOX_DISPLAY_NOT_CHECKED,
  dropdownOptions: [],
};
